import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail, ChevronRight, DollarSign, Star, Users, TrendingUp, Search, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const MyClients = () => {
  const { user, sessionToken } = useUnifiedAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('last_visit'); // last_visit, total_spent, total_visits, name

  useEffect(() => {
    const userId = user?.id || user?.user_id;
    if (userId) {
      fetchClients();
    }
  }, [user]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionToken || localStorage.getItem('sessionToken');
      const providerId = user?.provider_id || user?.id || user?.user_id;
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      const [bookedRes, importedRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers/${providerId}/clients`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider/clients/imported?limit=500`, { headers }),
      ]);

      if (!bookedRes.ok) {
        throw new Error('Failed to fetch clients');
      }

      const data = await bookedRes.json();
      const booked = data.clients || [];

      // Imported contacts have no bookings yet, so merge them in rather than
      // leaving the provider with an invisible half of their client list.
      let merged = booked;
      if (importedRes.ok) {
        const importedData = await importedRes.json();
        const known = new Set();
        booked.forEach((c) => {
          if (c.phone) known.add(c.phone.replace(/[\s-]/g, ''));
          if (c.email) known.add(c.email.toLowerCase());
        });
        const extras = (importedData.items || [])
          .filter((it) => {
            const phone = (it.phone || '').replace(/[\s-]/g, '');
            const email = (it.email || '').toLowerCase();
            return !(phone && known.has(phone)) && !(email && known.has(email));
          })
          .map((it) => ({
            id: it.id,
            name: it.name || 'Unknown',
            email: it.email || '',
            phone: it.phone || '',
            is_guest: false,
            is_imported: true,
            appointments: [],
            analytics: {
              total_visits: 0,
              completed_visits: 0,
              total_spent: 0,
              last_visit: null,
              favorite_service: null,
            },
          }));
        merged = [...booked, ...extras];
      }

      setClients(merged);
      setSummary({ ...(data.summary || {}), total_clients: merged.length });
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.phone?.includes(query)
    );
  });

  // Sort clients
  const sortedClients = [...filteredClients].sort((a, b) => {
    switch (sortBy) {
      case 'total_spent':
        return b.analytics.total_spent - a.analytics.total_spent;
      case 'total_visits':
        return b.analytics.total_visits - a.analytics.total_visits;
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'last_visit':
      default:
        return (b.analytics.last_visit || '').localeCompare(a.analytics.last_visit || '');
    }
  });

  // Format phone for WhatsApp (remove spaces, dashes, keep + and numbers)
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    return phone.replace(/[\s-]/g, '').replace(/^\+/, '');
  };

  // Handle click to call
  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  // Handle click to WhatsApp
  const handleWhatsApp = (phone, clientName) => {
    if (phone) {
      const formattedPhone = formatPhoneForWhatsApp(phone);
      const message = encodeURIComponent(`Hi ${clientName}, `);
      window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
    }
  };

  // Handle click to email
  const handleEmail = (email, clientName) => {
    if (email) {
      window.location.href = `mailto:${email}?subject=Hello from ${user?.name || 'Your Provider'}`;
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get days since last visit
  const getDaysSinceLastVisit = (lastVisit) => {
    if (!lastVisit) return null;
    const last = new Date(lastVisit);
    const today = new Date();
    const diffTime = today - last;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get client status badge
  const getClientStatus = (client) => {
    const daysSince = getDaysSinceLastVisit(client.analytics.last_visit);
    if (daysSince === null) return { label: 'New', color: 'bg-blue-100 text-blue-800' };
    if (daysSince <= 30) return { label: 'Active', color: 'bg-green-100 text-green-800' };
    if (daysSince <= 90) return { label: 'Regular', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Inactive', color: 'bg-red-100 text-red-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600 mb-4">Error: {error}</p>
        <button
          onClick={fetchClients}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Clients</h1>
          <p className="text-gray-600">Manage and connect with your clients</p>
        </div>
        <button
          onClick={fetchClients}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_clients || 0}</p>
              <p className="text-sm text-gray-500">Total Clients</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.active_clients || 0}</p>
              <p className="text-sm text-gray-500">Active Clients</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{(summary.total_revenue || 0).toFixed(0)} KWD</p>
              <p className="text-sm text-gray-500">Total Revenue</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Star className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{(summary.average_client_value || 0).toFixed(0)} KWD</p>
              <p className="text-sm text-gray-500">Avg. Client Value</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Sort */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="last_visit">Sort by Last Visit</option>
            <option value="total_spent">Sort by Total Spent</option>
            <option value="total_visits">Sort by Total Visits</option>
            <option value="name">Sort by Name</option>
          </select>
        </div>
      </div>

      {/* Clients List */}
      {sortedClients.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Clients Found</h3>
          <p className="text-gray-500">
            {searchQuery ? 'Try a different search term' : 'Clients will appear here after they book appointments'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedClients.map((client) => {
            const status = getClientStatus(client);
            return (
              <div
                key={client.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {/* Client Header */}
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Client Info */}
                    <button
                      onClick={() => navigate(`/provider/clients/${client.id}`)}
                      data-testid={`client-open-profile-${client.id}`}
                      className="flex items-start gap-4 text-start group"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {client.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 group-hover:underline">{client.name || 'Unknown'}</h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                            {status.label}
                          </span>
                          {client.is_guest && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              Guest
                            </span>
                          )}
                          {client.is_imported && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-violet-100 text-violet-700">
                              Imported
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-gray-500">
                          {client.phone && (
                            <span className="numeric-ltr flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {client.phone}
                            </span>
                          )}
                          {client.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {client.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2">
                      {client.phone && (
                        <>
                          <button
                            onClick={() => handleCall(client.phone)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                            title="Call"
                          >
                            <Phone className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleWhatsApp(client.phone, client.name)}
                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                            title="WhatsApp"
                          >
                            <MessageCircle className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {client.email && (
                        <button
                          onClick={() => handleEmail(client.email, client.name)}
                          className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors"
                          title="Email"
                        >
                          <Mail className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500">Total Visits</p>
                      <p className="font-semibold text-gray-900">{client.analytics.total_visits}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Spent</p>
                      <p className="font-semibold text-gray-900">{client.analytics.total_spent.toFixed(2)} KWD</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Last Visit</p>
                      <p className="font-semibold text-gray-900">{formatDate(client.analytics.last_visit)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Favorite Service</p>
                      <p className="font-semibold text-gray-900 truncate">{client.analytics.favorite_service || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Open full profile */}
                  <button
                    onClick={() => navigate(`/provider/clients/${client.id}`)}
                    data-testid={`client-view-profile-btn-${client.id}`}
                    className="flex items-center justify-center gap-2 w-full mt-4 pt-4 border-t border-gray-100 text-sm font-semibold text-blue-600 hover:text-blue-800"
                  >
                    View full profile
                    <ChevronRight className="w-4 h-4 rtl:rotate-180" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyClients;
